/**
 * A drop's gateway: the cell says "auto" (the container's strategy) or pins
 * its own, then what it goes through today (GatewayCellLabel); the picker
 * offers Automatic and every gateway of the pool with its sender.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { slotChoiceKeys } from './slot-choice-keys';
import * as Popover from '@radix-ui/react-popover';
import { Check, Search, Shuffle } from 'lucide-react';

import { useCountry } from '@/hooks';

import type { DayPlanDropRow, DraftGateway, DraftSlot } from '../../../../../drop-generator.types';
import type { SlotPatch } from '../../../state/slots/slot-ops';
import { GatewayCellLabel } from './cells/gateway/GatewayCellLabel';
import { LaneOption } from './cells/LaneOption';
import { gatewayAutoWords, slotGatewayLabel } from './slot-gateway-labels';

export const DropRoutePicker: FC<{
  slot: DraftSlot;
  gateway: DraftGateway;
  countryKey?: string;
  position: number;
  planned?: DayPlanDropRow;
  /** 19/10/2026 — a route test words its automatic gateway as turns / first gateway. */
  routeTest?: boolean;
  onPatch: (patch: SlotPatch) => void;
}> = ({ slot, gateway, countryKey = '', position, planned, routeTest = false, onPatch }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const country = useCountry(countryKey);
  const nameOf = (id: number) => country.data?.routes.find((route) => route.id === id)?.name ?? `Gateway ${id}`;
  const label = slotGatewayLabel({ slot, gateway, planned, nameOf });
  const auto = gatewayAutoWords(gateway.strategy, routeTest);
  const visible = gateway.pool.filter((lane) =>
    `${nameOf(lane.routeId)} ${lane.senders.ids.join(' ')}`.toLowerCase().includes(query.toLowerCase().trim()),
  );
  const choose = (id: string | null) => {
    const lane = gateway.pool.find((row) => row.id === id);
    onPatch({ routeId: lane?.routeId ?? null, gatewayRowId: lane?.id ?? null });
    setOpen(false);
  };
  return (
    <Popover.Root
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        setQuery('');
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={`Drop ${position}: choose gateway and SID`}
          title={`${label.title} · ${label.detail}${label.automatic ? ` — automatic, ${auto.name.toLowerCase()}: ${auto.caption}` : ' — fixed on this drop'}`}
          className="flex min-h-6 w-full min-w-0 items-center gap-1.5 rounded-md px-1.5 py-0.5 text-left transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary data-[state=open]:bg-muted/70"
        >
          <GatewayCellLabel label={label} autoTitle={`Automatic · ${auto.name}${auto.caption ? ` — ${auto.caption}` : ''}`} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          collisionPadding={12}
          onKeyDown={slotChoiceKeys}
          className="z-[250] w-[min(440px,calc(100vw-24px))] rounded-xl border bg-popover p-2 text-popover-foreground shadow-xl"
        >
          <p className="px-2 pb-2 pt-1 text-xs font-semibold">Gateway &amp; sender ID</p>
          <label className="mx-1 flex h-8 items-center gap-2 rounded-md border border-input px-2">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              autoFocus
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Find gateway or SID…"
              aria-label={`Search gateways for drop ${position}`}
              className="min-w-0 flex-1 bg-transparent text-xs outline-none"
            />
          </label>
          <div role="listbox" aria-label={`Gateways for drop ${position}`} className="mt-1 max-h-64 overflow-y-auto overscroll-contain">
            {!query.trim() ? (
              <button
                type="button"
                role="option"
                aria-selected={label.automatic}
                onClick={() => choose(null)}
                className="flex w-full items-center gap-2 rounded-md p-2 text-left text-xs hover:bg-accent"
              >
                <Shuffle className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1">
                  <span className="block">Automatic · {auto.name}</span>
                  {auto.caption ? <span className="block text-[11px] text-muted-foreground">{auto.caption}</span> : null}
                </span>
                {label.automatic ? <Check className="h-3.5 w-3.5 text-primary" /> : null}
              </button>
            ) : null}
            {visible.map((lane) => (
              <LaneOption
                key={lane.id}
                gateway={gateway}
                index={gateway.pool.indexOf(lane)}
                name={nameOf(lane.routeId)}
                selected={slot.gatewayRowId === lane.id}
                onChoose={() => choose(lane.id)}
              />
            ))}
            {!visible.length ? (
              <p className="p-2 text-xs text-muted-foreground">
                {gateway.pool.length ? 'No matching gateways or sender IDs.' : 'Add gateways and sender IDs in the gateway settings first.'}
              </p>
            ) : null}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
